import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";

const SEV_COLORS = { critical: "#FF3B30", high: "#FF9500", medium: "#FFCC00", low: "#00F0FF" };

function Panel({ title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C0C14] border border-[#1F1F33] rounded-none p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-4">{title}</div>
      {children}
    </div>
  );
}

const tooltipStyle = {
  contentStyle: { background: "#12121E", border: "1px solid #1F1F33", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 },
  labelStyle: { color: "#fff" },
};

export default function Analytics() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/analytics");
    setData(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-xs text-[#71717A] font-mono">Loading analytics…</div>;

  return (
    <div data-testid="analytics-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Intelligence</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Threat Analytics</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Threats Over Time · 7 Days" testid="chart-timeline">
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={data.timeline} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F1F33" vertical={false} />
                <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#1F1F33" }} />
                <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="threats" stroke="#00F0FF" strokeWidth={2} dot={{ fill: "#00F0FF", r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Severity Distribution" testid="chart-severity">
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data.severity_distribution}
                  dataKey="value" nameKey="name"
                  innerRadius={60} outerRadius={95} paddingAngle={2} stroke="none"
                >
                  {data.severity_distribution.map((e) => (
                    <Cell key={e.name} fill={SEV_COLORS[e.name] || "#71717A"} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} />
                <Legend
                  wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}
                  iconType="square"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Threat Categories" testid="chart-categories">
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={data.categories} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1F1F33" horizontal={false} />
                <XAxis type="number" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#1F1F33" }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="#71717A" fontSize={10} width={110} tickLine={false} axisLine={false} />
                <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="value" fill="#00F0FF" barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Severity Breakdown" testid="severity-breakdown">
          <div className="space-y-4 pt-2">
            {data.severity_distribution.map((s) => {
              const total = data.severity_distribution.reduce((a, b) => a + b.value, 0) || 1;
              const pct = Math.round((s.value / total) * 100);
              return (
                <div key={s.name}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="uppercase tracking-[0.15em]" style={{ color: SEV_COLORS[s.name] }}>{s.name}</span>
                    <span className="text-[#A1A1AA] tabular-nums">{s.value} · {pct}%</span>
                  </div>
                  <div className="h-2 bg-[#1F1F33]">
                    <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: SEV_COLORS[s.name] }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>
    </div>
  );
}
